/**
 * Hook for managing favorited stock solutions.
 *
 * Pi-backed (namespace `stock-favorites`, a JSON array of slug
 * strings) via `useServerBackedState`. The legacy localStorage copy
 * (`reefapp.stockSolutions.favorites`) seeds the default and is never
 * deleted.
 */
import { useCallback, useMemo, useRef, useState } from 'react';

import { useServerBackedState } from '../hooks/useServerBackedState';

/** Legacy browser-only key — read as the seed default, never deleted. */
const LEGACY_FAVORITES_KEY = 'reefapp.stockSolutions.favorites';

/** `/api/userdata` namespace holding favorite slugs on the Pi. */
export const STOCK_FAVORITES_NAMESPACE = 'stock-favorites';

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.every((item): item is string => typeof item === 'string')
  );
}

function readLegacyFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(LEGACY_FAVORITES_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return isStringArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export interface UseStockSolutionsFavoritesResult {
  favorites: string[];
  isFavorite(slug: string): boolean;
  toggleFavorite(slug: string): void;
}

export function useStockSolutionsFavorites(): UseStockSolutionsFavoritesResult {
  const [legacyDefault] = useState(readLegacyFavorites);
  const { value, setValue } = useServerBackedState<string[]>(
    STOCK_FAVORITES_NAMESPACE,
    legacyDefault,
  );
  // Tolerate a malformed server blob.
  const favorites = useMemo(
    () => (isStringArray(value) ? value : []),
    [value],
  );
  const favoritesRef = useRef(favorites);
  favoritesRef.current = favorites;

  const toggleFavorite = useCallback(
    (slug: string) => {
      const prev = favoritesRef.current;
      const next = prev.includes(slug)
        ? prev.filter((s) => s !== slug)
        : [...prev, slug];
      setValue(next);
    },
    [setValue],
  );

  const isFavorite = useCallback(
    (slug: string) => favorites.includes(slug),
    [favorites],
  );

  return useMemo(
    () => ({ favorites, isFavorite, toggleFavorite }),
    [favorites, isFavorite, toggleFavorite],
  );
}
